import { LanguageContext } from "context/LanguageContext"
import { UploadContext } from "context/UploadContext"
import isDisposableEmail from "lib/disposable-email-domains"
import Button from "lib/components/Button"
import Input from "lib/components/Input"
import InputFile from "lib/components/InputFile"
import InputPhone from "lib/components/InputPhone"
import Select from "lib/components/Select"
import TextArea from "lib/components/TextArea"
import fetcher from "lib/fetcher"
import {
  isLinkedinUrl,
  isValidName,
  normalizeLinkedinUrl,
  sanitizeName,
  sanitizeText,
} from "lib/helpers"
import useRecaptcha from "hooks/useRecaptcha"
import React, { useContext, useMemo, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import useSWR from "swr"

function JoinProviderFrom() {
  const { uploadFile, progressNumber } = useContext(UploadContext)
  const { useTranslation: t } = useContext(LanguageContext)
  const [dataCategory, setDataCategory] = useState([])
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [loadingUpload, setLoadingUpload] = useState(false)

  const {
    ref: captchaRef,
    isPassed: isRecaptchaPassed,
    getToken: getCaptchaToken,
    reset: resetCaptcha,
  } = useRecaptcha()

  const { data: categoryList } = useSWR("/api/categories/list", fetcher)

  const methods = useForm({
    defaultValues: {
      company_legal_name: null,
      company_pic_first_name: null,
      company_pic_last_name: null,
      company_pic_job_title: null,
      company_pic_email: null,
      company_pic_phone_number: null,
      company_website: null,
      company_profile: null,
      linkedin_page: null,
      expertise: [],
      notes: null,
    },
    shouldFocusError: true,
  })

  useMemo(() => {
    if (categoryList?.categories.length > 0) {
      const categoryData = categoryList?.categories.map((category) => {
        const optionsData = [...category.options, { label: category.label, value: category.value }]
        return {
          label: category.label,
          value: category.value,
          options: optionsData,
        }
      })
      setDataCategory(categoryData)
    }
    return () => {
      setDataCategory([])
    }
  }, [categoryList])

  const onSubmit = async (data) => {
    setLoadingSubmit(true)
    try {
      const listDataExpertise = data.expertise.map((item) => item.value)
      // Sanitize all user inputs to prevent XSS
      const dataPayload = {
        company_legal_name: sanitizeText(data.company_legal_name, 200),
        company_pic_first_name: sanitizeName(data.company_pic_first_name, 25),
        company_pic_last_name: sanitizeName(data.company_pic_last_name, 25),
        company_pic_email: data.company_pic_email,
        company_pic_job_title: data.company_pic_job_title ? sanitizeText(data.company_pic_job_title, 200) : null,
        company_pic_phone_number: data.company_pic_phone_number,
        company_website: data.company_website,
        company_profile: data.company_profile,
        linkedin_page: normalizeLinkedinUrl(data.linkedin_page) || null,
        expertise: listDataExpertise,
        notes: data.notes ? sanitizeText(data.notes, 2000) : null,
        recaptcha_token: getCaptchaToken(),
      }

      await fetcher("/api/trainer/create-provider", "POST", dataPayload)
      toast.success(t("we_already_received_your_message"))
      setLoadingSubmit(false)
      methods.reset({
        company_legal_name: null,
        company_pic_first_name: null,
        company_pic_last_name: null,
        company_pic_job_title: null,
        company_pic_email: null,
        company_pic_phone_number: null,
        company_website: null,
        company_profile: null,
        linkedin_page: null,
        expertise: [],
        notes: null,
      })
    } catch (err) {
      setLoadingSubmit(false)
      // Input validation runs before the Hasura mutation, so a rejection never consumed the
      // reCAPTCHA token — keep it so the user doesn't have to solve the challenge again.
      if (err.message !== "Invalid input detected") resetCaptcha()
      if (err.message === "ErrTrainerNameAlreadyExist") {
        toast.error(t("same_name_provider"))
      } else if (err.message === "Invalid input detected") {
        toast.error(t("invalid_input_detected_hint"))
      } else if (err.message === "ErrInvalidRecaptcha") {
        toast.error(t("invalid_recaptcha"))
      } else {
        toast.error(`Oops: ${err.message}`)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-x-4 ml-0 sm:ml-10 mt-0 sm:-mt-4">
          <div className="col-span-2 form-control">
            <Input
              label={`${t("training_provider_name")} *`}
              placeholder={t("input_training_provider_name")}
              type="text"
              error={!!methods.formState.errors.company_legal_name}
              message={
                methods.formState.errors.company_legal_name &&
                methods.formState.errors.company_legal_name.message &&
                typeof methods.formState.errors.company_legal_name.message === "string"
                  ? methods.formState.errors.company_legal_name.message
                  : ""
              }
              {...methods.register("company_legal_name", {
                required: t("training_provider_name_required"),
              })}
            />
          </div>

          <div className="form-control">
            <Input
              label={`${t("pic_first_name")} *`}
              placeholder={t("input_pic_first_name")}
              type="text"
              {...methods.register("company_pic_first_name", {
                required: t("pic_first_name_required"),
                maxLength: {
                  value: 25,
                  message: t("first_name_max_length") || "First name is too long",
                },
                validate: (value) =>
                  isValidName(value, 25) || t("invalid_name_format") || "Invalid name format",
              })}
              error={!!methods.formState.errors.company_pic_first_name}
              message={
                methods.formState.errors.company_pic_first_name &&
                methods.formState.errors.company_pic_first_name.message &&
                typeof methods.formState.errors.company_pic_first_name.message === "string"
                  ? methods.formState.errors.company_pic_first_name.message
                  : ""
              }
            />
          </div>
          <div className="form-control">
            <Input
              label={`${t("pic_last_name")} *`}
              placeholder={t("input_pic_last_name")}
              type="text"
              {...methods.register("company_pic_last_name", {
                required: t("pic_last_name_required"),
                maxLength: {
                  value: 25,
                  message: t("last_name_max_length") || "Last name is too long",
                },
                validate: (value) =>
                  isValidName(value, 25) || t("invalid_name_format") || "Invalid name format",
              })}
              error={!!methods.formState.errors.company_pic_last_name}
              message={
                methods.formState.errors.company_pic_last_name &&
                methods.formState.errors.company_pic_last_name.message &&
                typeof methods.formState.errors.company_pic_last_name.message === "string"
                  ? methods.formState.errors.company_pic_last_name.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("pic_job_title")} *`}
              placeholder={t("input_pic_job_title")}
              type="text"
              {...methods.register("company_pic_job_title", {
                required: t("pic_job_title_required"),
              })}
              error={!!methods.formState.errors.company_pic_job_title}
              message={
                methods.formState.errors.company_pic_job_title &&
                methods.formState.errors.company_pic_job_title.message &&
                typeof methods.formState.errors.company_pic_job_title.message === "string"
                  ? methods.formState.errors.company_pic_job_title.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("pic_email")} *`}
              placeholder={t("input_pic_email")}
              type="text"
              {...methods.register("company_pic_email", {
                required: t("pic_email_required"),
                pattern: {
                  value: /^[A-Z0-9._]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("invalid_email_address"),
                },
                validate: (value) => (value && isDisposableEmail(value) ? t("invalid_email_address") : true),
              })}
              error={!!methods.formState.errors.company_pic_email}
              message={
                methods.formState.errors.company_pic_email &&
                methods.formState.errors.company_pic_email.message &&
                typeof methods.formState.errors.company_pic_email.message === "string"
                  ? methods.formState.errors.company_pic_email.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control">
            <Controller
              control={methods.control}
              name="company_pic_phone_number"
              rules={{
                required: t("phone_number_required"),
                minLength: {
                  value: 8,
                  message: t("phone_number_must_match_rule"),
                },
                maxLength: {
                  value: 14,
                  message: t("phone_number_must_match_rule"),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <InputPhone
                  defaultCountry="id"
                  countryCodeEditable
                  enableSearch
                  disableSearchIcon
                  label={`${t("pic_phone")} *`}
                  placeholder="628122XXXXXXX"
                  error={!!methods.formState.errors.company_pic_phone_number}
                  message={
                    methods.formState.errors.company_pic_phone_number &&
                    methods.formState.errors.company_pic_phone_number.message &&
                    typeof methods.formState.errors.company_pic_phone_number.message === "string"
                      ? methods.formState.errors.company_pic_phone_number.message
                      : ""
                  }
                  value={value}
                  onChange={(event) => {
                    onChange(event)
                    methods.trigger("company_pic_phone_number")
                  }}
                />
              )}
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("linkedin_page")}`}
              placeholder={t("input_linkedin_page")}
              type="text"
              {...methods.register("linkedin_page", {
                validate: (value) =>
                  isLinkedinUrl(value) ||
                  t("invalid_linkedin_page") ||
                  "This is not a LinkedIn link",
              })}
              error={!!methods.formState.errors.linkedin_page}
              message={
                methods.formState.errors.linkedin_page &&
                methods.formState.errors.linkedin_page.message &&
                typeof methods.formState.errors.linkedin_page.message === "string"
                  ? methods.formState.errors.linkedin_page.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("website")} *`}
              placeholder={t("input_website")}
              type="text"
              {...methods.register("company_website", {
                required: t("website_required"),
              })}
              error={!!methods.formState.errors.company_website}
              message={
                methods.formState.errors.company_website &&
                methods.formState.errors.company_website.message &&
                typeof methods.formState.errors.company_website.message === "string"
                  ? methods.formState.errors.company_website.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control">
            <Select
              isMulti
              name="expertise"
              options={dataCategory}
              label={`${t("expertise")} *`}
              placeholder={t("input_expertise")}
              {...methods.register("expertise", {
                required: t("expertise_required"),
              })}
              value={methods.watch("expertise")}
              onChange={(eventTarget) => {
                methods.setValue("expertise", eventTarget, { shouldDirty: true })
                methods.clearErrors("expertise")
              }}
              maxLimit={3}
              error={!!methods.formState.errors.expertise}
              message={
                methods.formState.errors.expertise &&
                methods.formState.errors.expertise.message &&
                typeof methods.formState.errors.expertise.message === "string"
                  ? methods.formState.errors.expertise.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control mt-4">
            <InputFile
              label={t("provider_profile")}
              name="provider_profile"
              placeholder=""
              value={methods.watch("company_profile")}
              loading={loadingUpload}
              progressNumber={progressNumber}
              {...methods.register("company_profile")}
              onChange={async (event) => {
                setLoadingUpload(true)
                await uploadFile(
                  event.target.files[0],
                  (responseData) => {
                    if (responseData.secure_url) {
                      methods.setValue("company_profile", responseData.secure_url, {
                        shouldDirty: true,
                      })
                      setLoadingUpload(false)
                      methods.clearErrors("company_profile")
                    }
                  },
                  "trm",
                  false,
                )
                event.target.value = null
              }}
              onCancel={() => {
                methods.setValue("company_profile", null)
                setLoadingUpload(false)
              }}
              accept="application/pdf"
            />
          </div>
          <div className="col-span-2 form-control">
            <TextArea
              label={`${t("things_we_need_company")} *`}
              placeholder={t("things_we_need_company_placeholder")}
              rows={5}
              {...methods.register("notes", {
                required: t("things_we_need_company_required"),
              })}
              error={!!methods.formState.errors.notes}
              message={
                methods.formState.errors.notes &&
                methods.formState.errors.notes.message &&
                typeof methods.formState.errors.notes.message === "string"
                  ? methods.formState.errors.notes.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2">
            <div className="flex items-center">
              <div className="hover:cursor-pointer" ref={captchaRef} />
            </div>
          </div>
          <div className="mt-4">
            {isRecaptchaPassed ? (
              <Button
                disabled={loadingSubmit}
                loading={loadingSubmit}
                type="submit"
                color="primary"
                variant="contained"
                fullWidth>
                {t("submit")}
              </Button>
            ) : (
              <Button disabled type="submit" color="primary" variant="contained" fullWidth>
                {t("submit")}
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default JoinProviderFrom
